import React, { useState } from "react"
import { Div, Text } from "atomize"
import Card from "../Card"
import { theme } from "../settings/config"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
	faFileInvoiceDollar,
	faHandHoldingDollar,
	faTableTennisPaddleBall,
	faChevronDown,
	faChevronUp,
	faTriangleExclamation,
	faLightbulb,
	faFireAlt,
} from "@fortawesome/free-solid-svg-icons"
import { formatAmount, groupCourtCostsByKind } from "../utils/billing.utils"
import LessonTypeLabel from "../atoms/LessonTypeLabel"
import Divider from "../atoms/Divider"
import HelpTip from "../help/HelpTip"
import { formatHoursAmount as hoursLabel } from "../utils/week.utils"

// Che campo era: quello liscio, o quello con i supplementi che il circolo
// fa pagare a parte. Quello liscio si chiama per differenza da quelli che
// ha accanto: "Senza riscaldamento" se c'è solo il riscaldamento, "Solo
// campo" se c'è anche la luce, e "Campo" se è l'unico.
const courtLabel = ({ usingLights, usingHeat }, groups) => {
	if (usingLights && usingHeat) return "Con luce e riscaldamento"
	if (usingLights) return "Con la luce"
	if (usingHeat) return "Con il riscaldamento"
	if (groups.some(group => group.usingLights)) return "Solo campo"
	if (groups.some(group => group.usingHeat)) return "Senza riscaldamento"
	return "Campo"
}

const courtKindIcons = {
	base: {
		icon: faTableTennisPaddleBall,
		color: theme.colors.gunmetal,
		bg: theme.colors.gunmetal_lighter,
	},
	lights: { icon: faLightbulb, color: theme.colors.yellow, bg: theme.colors.yellow_light },
	heat: { icon: faFireAlt, color: theme.colors.red, bg: theme.colors.red_light },
	lightsHeat: { icon: faFireAlt, color: theme.colors.red, bg: theme.colors.red_light },
}

// Le ore per tariffa: di solito una sola, due se il circolo ha cambiato
// prezzo a metà periodo, e allora il conto si vede pezzo per pezzo.
const feesDetail = fees =>
	fees.map(({ fee, hours }) => `${hoursLabel(hours)} × ${formatAmount(fee)} €/h`).join(" · ")

const SectionHeader = ({ icon, color, bg, title, amount, sign, expanded, onToggle }) => (
	<Div
		d="flex"
		align="center"
		justify="space-between"
		m={{ b: "0.5rem" }}
		style={{ cursor: "pointer" }}
		onClick={onToggle}>
		<Div d="flex" align="center" style={{ gap: "0.5rem" }}>
			<Div
				w="28px"
				h="28px"
				rounded="circle"
				bg={bg}
				d="flex"
				align="center"
				justify="center">
				<FontAwesomeIcon icon={icon} color={color} size="xs" />
			</Div>
			<Text
				textWeight="bold"
				textSize="caption"
				textColor={theme.colors.text_light}
				textTransform="uppercase">
				{title}
			</Text>
		</Div>
		<Div d="flex" align="center" style={{ gap: "0.5rem" }}>
			<Text textSize="subheader" textWeight="bold" textColor={color}>
				{sign}
				{formatAmount(amount)} €
			</Text>
			<FontAwesomeIcon
				icon={expanded ? faChevronUp : faChevronDown}
				color={theme.colors.text_light}
				size="xs"
			/>
		</Div>
	</Div>
)

const Row = ({ label, detail, amount, sign, color }) => (
	<Div
		d="flex"
		align="center"
		justify="space-between"
		p={{ y: "0.35rem" }}
		border="bottom"
		borderColor={theme.colors.gray_light}>
		<Div d="flex" flexDir="column">
			<Text textSize="subheader">{label}</Text>
			{detail && (
				<Text textSize="caption" textColor={theme.colors.text_light}>
					{detail}
				</Text>
			)}
		</Div>
		<Text textSize="subheader" textWeight="bold" textColor={color}>
			{sign}
			{formatAmount(amount)} €
		</Text>
	</Div>
)

// Un blocco dentro la scheda: il fondo caldo, gli angoli tondi, niente
// bordi. A destra lascia lo spazio della freccia delle sezioni, così gli
// importi cadono sotto il totale della sezione.
const WarmBlock = ({ children }) => (
	<Div
		bg={theme.colors.background}
		rounded="15px"
		p={{ y: "0.5rem", l: "0.75rem", r: "1.25rem" }}
		d="flex"
		flexDir="column"
		style={{ gap: "0.35rem" }}>
		{children}
	</Div>
)

const BlockRow = ({ label, detail, amount, sign, color }) => (
	<Div d="flex" align="center" justify="space-between" style={{ gap: "0.5rem" }}>
		<Div d="flex" flexDir="column" style={{ minWidth: 0 }}>
			<Text textSize="paragraph">{label}</Text>
			{detail && (
				<Text textSize="caption" textColor={theme.colors.text_light}>
					{detail}
				</Text>
			)}
		</Div>
		<Text
			textSize="paragraph"
			textWeight="bold"
			textColor={color}
			style={{ whiteSpace: "nowrap" }}>
			{sign}
			{formatAmount(amount)} €
		</Text>
	</Div>
)

// Un tipo di campo, e dentro chi l'ha usato: le lezioni e le tue partite.
// Le righe partono dove parte il nome del blocco, non dal pallino.
const CourtKindBlock = ({ group, label }) => {
	const { icon, color, bg } = courtKindIcons[group.kind]
	return (
		<WarmBlock>
			<Div d="flex" align="center" justify="space-between" style={{ gap: "0.5rem" }}>
				<Div d="flex" align="center" style={{ gap: "0.5rem" }}>
					<Div
						w="22px"
						h="22px"
						rounded="circle"
						bg={bg}
						d="flex"
						align="center"
						justify="center"
						flexShrink="0">
						<FontAwesomeIcon icon={icon} color={color} size="2xs" />
					</Div>
					<Text textWeight="bold" textColor={theme.colors.gunmetal}>
						{label}
					</Text>
				</Div>
				<Text
					textWeight="bold"
					textColor={theme.colors.gunmetal}
					style={{ whiteSpace: "nowrap" }}>
					- {formatAmount(group.total)} €
				</Text>
			</Div>
			<Div d="flex" flexDir="column" p={{ l: "30px" }} style={{ gap: "0.35rem" }}>
				{group.lessons && (
					<BlockRow
						label="Lezioni"
						detail={feesDetail(group.lessons.fees)}
						amount={group.lessons.cost}
						sign="- "
						color={theme.colors.red}
					/>
				)}
				{group.matches && (
					<BlockRow
						label="Quando giochi tu"
						detail={feesDetail(group.matches.fees)}
						amount={group.matches.cost}
						sign="- "
						color={theme.colors.red}
					/>
				)}
			</Div>
		</WarmBlock>
	)
}

const lessonsLabel = lessons => `${lessons} ${lessons < 2 ? "lezione" : "lezioni"}`

// Le volte di chi viene al posto di un assente sono sostituzioni; chi viene
// in più, o un po' l'uno e un po' l'altro, fa lezioni.
const heldForSchoolDetail = ({ lessons, substitutions }) =>
	substitutions === lessons
		? `${lessons} ${lessons < 2 ? "sostituzione" : "sostituzioni"}`
		: lessonsLabel(lessons)

// Quanto ha pagato al circolo un allievo, e quanto campo il circolo si è
// tenuto: la differenza è quello che si fattura.
const paidToSchoolDetail = ({ lessons, paid, courtShare }) =>
	`${lessonsLabel(lessons)} · pagati ${formatAmount(paid)} €` +
	(courtShare > 0 ? `, campo − ${formatAmount(courtShare)} €` : "")

const EarningsCard = ({ earnings, getLessonType }) => {
	const [showPlayers, setShowPlayers] = useState(false)
	const [showPaidToSchool, setShowPaidToSchool] = useState(false)
	// le sezioni partono chiuse: in vista resta il totale di ognuna
	const [openSections, setOpenSections] = useState({})

	const toggleSection = section =>
		setOpenSections(prev => ({ ...prev, [section]: !prev[section] }))

	if (!earnings) return null

	const { schoolBilling, privateIncome, courtCosts, net } = earnings
	const heldForSchool = earnings.heldForSchool ?? { total: 0, byPlayer: {} }
	const paidToSchool = earnings.paidToSchool ?? { total: 0, byPlayer: {} }
	const withheldBySchool = courtCosts.withheldBySchool ?? 0

	const hasSchoolBilling = schoolBilling.hours > 0 || schoolBilling.hoursWithoutRate > 0
	// gli allievi che hanno pagato il circolo invece di te: anche quelli si
	// fatturano al circolo, e stanno nella stessa sezione
	const paidToSchoolList = Object.entries(paidToSchool.byPlayer).sort(
		([, a], [, b]) => b.amount - a.amount,
	)
	const hasPaidToSchool = paidToSchoolList.length > 0
	const hasSchoolSection = hasSchoolBilling || hasPaidToSchool
	const hasPrivate = privateIncome.hours > 0
	// il campo delle tue partite sta nello stesso costo campi delle lezioni
	const matches = earnings.matches ?? { byFee: {}, total: 0, hours: 0, count: 0, withoutFee: 0 }
	const courtGroups = groupCourtCostsByKind({ courtCosts, matches })
	const hasCourtCosts =
		courtCosts.total > 0 ||
		withheldBySchool > 0 ||
		courtCosts.lessonsWithoutFee > 0 ||
		courtCosts.lessonsPaidByPlayers > 0 ||
		matches.count > 0

	const hasHeldForSchool = heldForSchool.total > 0

	if (!hasSchoolSection && !hasPrivate && !hasCourtCosts && !hasHeldForSchool) return null

	const lessonTypeName = lessonType => (
		<LessonTypeLabel lessonTypeID={lessonType} lessonType={getLessonType?.(lessonType)} />
	)

	const playersList = Object.entries(privateIncome.byPlayer).sort(
		([, a], [, b]) => b.amount - a.amount,
	)

	return (
		<Card bg={theme.colors.surface}>
			<Div d="flex" align="center" justify="space-between" m={{ b: "1rem" }}>
				<Div d="flex" align="center" style={{ gap: "0.5rem" }}>
					<Div
						w="40px"
						h="40px"
						rounded="circle"
						bg={theme.colors.yellow_light}
						d="flex"
						align="center"
						justify="center">
						<FontAwesomeIcon icon={faFileInvoiceDollar} color={theme.colors.yellow} />
					</Div>
					<HelpTip tip="compensi">
						<Text textWeight="bold" textSize="title">
							Compensi
						</Text>
					</HelpTip>
				</Div>
				<Div
					p="0.25rem 0.75rem"
					rounded="circle"
					bg={theme.colors.gunmetal_fill}
					d="flex"
					align="center">
					<Text textWeight="bold" textColor="white" textSize="subheader">
						{formatAmount(net)} €
					</Text>
				</Div>
			</Div>

			{/* ── DA FATTURARE AL CIRCOLO ───────────────────────────────── */}
			{hasSchoolSection && (
				<Div m={{ b: openSections.schoolBilling ? "1rem" : "0.5rem" }}>
					<SectionHeader
						icon={faFileInvoiceDollar}
						color={theme.colors.yellow}
						bg={theme.colors.yellow_light}
						title="Da fatturare al circolo"
						amount={schoolBilling.total + paidToSchool.total}
						expanded={openSections.schoolBilling}
						onToggle={() => toggleSection("schoolBilling")}
					/>
					{openSections.schoolBilling &&
						Object.entries(schoolBilling.byType)
							.sort(([, a], [, b]) => b.amount - a.amount)
							.map(([lessonType, entry]) => (
								<Row
									key={lessonType}
									label={lessonTypeName(lessonType)}
									// due tariffe diverse nel periodo: le ore sono
									// quelle, ma non c'è un "€/h" solo da mostrare
									detail={
										entry.rate === null
											? hoursLabel(entry.hours)
											: `${hoursLabel(entry.hours)} × ${formatAmount(entry.rate)} €/h`
									}
									amount={entry.amount}
									color={theme.colors.gunmetal}
								/>
							))}

					{openSections.schoolBilling && schoolBilling.hoursWithoutRate > 0 && (
						<Div
							d="flex"
							align="center"
							style={{ gap: "0.5rem" }}
							m={{ t: "0.5rem" }}
							p="0.5rem 0.75rem"
							rounded="15px"
							bg={theme.colors.yellow_light}>
							<FontAwesomeIcon
								icon={faTriangleExclamation}
								color={theme.colors.yellow}
								size="sm"
							/>
							<Text textSize="caption" textColor={theme.colors.yellow}>
								{hoursLabel(schoolBilling.hoursWithoutRate)} senza tariffa al
								circolo per quella data
							</Text>
						</Div>
					)}

					{/* Le private pagate al circolo invece che a te: il
					    circolo ha i tuoi soldi, tolta la quota di campo che
					    si è tenuto. Una riga sola, e toccandola chi sono. */}
					{openSections.schoolBilling && hasPaidToSchool && (
						<>
							<Row
								label="Pagate al circolo dagli allievi"
								detail="quello che hanno pagato, tolto il campo"
								amount={paidToSchool.total}
								color={theme.colors.gunmetal}
							/>
							<Div
								d="flex"
								align="center"
								justify="space-between"
								p={{ y: "0.5rem" }}
								style={{ cursor: "pointer" }}
								onClick={() => setShowPaidToSchool(!showPaidToSchool)}>
								<Text textSize="caption" textColor={theme.colors.text_light}>
									Dettaglio per allievo ({paidToSchoolList.length})
								</Text>
								<FontAwesomeIcon
									icon={showPaidToSchool ? faChevronUp : faChevronDown}
									color={theme.colors.text_light}
									size="xs"
								/>
							</Div>
							{showPaidToSchool &&
								paidToSchoolList.map(([playerID, entry]) => (
									<Row
										key={playerID}
										label={entry.playerName}
										detail={paidToSchoolDetail(entry)}
										amount={entry.amount}
										color={theme.colors.yellow}
									/>
								))}
						</>
					)}
				</Div>
			)}

			{/* ── INCASSI PRIVATE ───────────────────────────────────────── */}
			{hasPrivate && (
				<Div m={{ b: openSections.privateIncome ? "1rem" : "0.5rem" }}>
					{hasSchoolSection && <Divider w="2px solid" m={{ b: "0.75rem" }} />}
					<SectionHeader
						icon={faHandHoldingDollar}
						color={theme.colors.green}
						bg={theme.colors.green_light}
						title="Incassi private"
						amount={privateIncome.total}
						expanded={openSections.privateIncome}
						onToggle={() => toggleSection("privateIncome")}
					/>
					{openSections.privateIncome &&
						Object.entries(privateIncome.byType)
							.sort(([, a], [, b]) => b.amount - a.amount)
							.map(([lessonType, entry]) => (
								<Row
									key={lessonType}
									label={lessonTypeName(lessonType)}
									detail={hoursLabel(entry.hours)}
									amount={entry.amount}
									color={theme.colors.gunmetal}
								/>
							))}

					{openSections.privateIncome && playersList.length > 0 && (
						<>
							<Div
								d="flex"
								align="center"
								justify="space-between"
								p={{ y: "0.5rem" }}
								style={{ cursor: "pointer" }}
								onClick={() => setShowPlayers(!showPlayers)}>
								<Text textSize="caption" textColor={theme.colors.text_light}>
									Dettaglio per cliente ({playersList.length})
								</Text>
								<FontAwesomeIcon
									icon={showPlayers ? faChevronUp : faChevronDown}
									color={theme.colors.text_light}
									size="xs"
								/>
							</Div>
							{showPlayers &&
								playersList.map(([playerID, entry]) => (
									<Row
										key={playerID}
										label={entry.playerName}
										detail={`${entry.lessons} ${
											entry.lessons < 2 ? "lezione" : "lezioni"
										} · ${hoursLabel(entry.hours)}`}
										amount={entry.amount}
										color={theme.colors.green}
									/>
								))}
						</>
					)}
				</Div>
			)}

			{/* ── COSTO CAMPI ───────────────────────────────────────────── */}
			{/* Uno solo, quello delle lezioni e quello di quando giochi tu:
			    aperto, un blocco per tipo di campo e dentro chi l'ha usato. */}
			{hasCourtCosts && (
				<Div m={{ b: openSections.courtCosts ? "1rem" : "0.5rem" }}>
					{(hasSchoolSection || hasPrivate) && (
						<Divider w="2px solid" m={{ b: "0.75rem" }} />
					)}
					<SectionHeader
						icon={faTableTennisPaddleBall}
						color={theme.colors.red}
						bg={theme.colors.red_light}
						title="Costo campi"
						amount={courtCosts.total + matches.total}
						sign="- "
						expanded={openSections.courtCosts}
						onToggle={() => toggleSection("courtCosts")}
					/>
					{openSections.courtCosts && (
						<Div d="flex" flexDir="column" style={{ gap: "0.5rem" }}>
							{courtGroups.map(group => (
								<CourtKindBlock
									key={group.kind}
									group={group}
									label={courtLabel(group, courtGroups)}
								/>
							))}

							{/* Il campo l'hai pagato tu, ma la quota di chi ha
							    pagato il circolo il circolo l'ha già tenuta: non
							    esce più dalle tue tasche. */}
							{withheldBySchool > 0 && (
								<WarmBlock>
									<BlockRow
										label="Trattenuto dal circolo"
										detail="la quota di campo di chi ha pagato il circolo"
										amount={withheldBySchool}
										sign="+ "
										color={theme.colors.green}
									/>
								</WarmBlock>
							)}

							{/* Le lezioni col campo pagato dagli allievi non
							    costano niente, ma vederle contate dice che non
							    sono state dimenticate. */}
							{courtCosts.lessonsPaidByPlayers > 0 && (
								<Div
									d="flex"
									align="center"
									style={{ gap: "0.5rem" }}
									p="0.5rem 0.75rem"
									rounded="15px"
									bg={theme.colors.green_light}>
									<FontAwesomeIcon
										icon={faHandHoldingDollar}
										color={theme.colors.green}
										size="sm"
									/>
									<Text textSize="caption" textColor={theme.colors.green}>
										{courtCosts.lessonsPaidByPlayers}{" "}
										{courtCosts.lessonsPaidByPlayers < 2 ? "lezione" : "lezioni"}{" "}
										col campo pagato dagli allievi
									</Text>
								</Div>
							)}

							{courtCosts.lessonsWithoutFee > 0 && (
								<Div
									d="flex"
									align="center"
									style={{ gap: "0.5rem" }}
									p="0.5rem 0.75rem"
									rounded="15px"
									bg={theme.colors.yellow_light}>
									<FontAwesomeIcon
										icon={faTriangleExclamation}
										color={theme.colors.yellow}
										size="sm"
									/>
									<Text textSize="caption" textColor={theme.colors.yellow}>
										{courtCosts.lessonsWithoutFee}{" "}
										{courtCosts.lessonsWithoutFee < 2 ? "lezione" : "lezioni"} senza
										tariffa campo per quella data
									</Text>
								</Div>
							)}

							{matches.withoutFee > 0 && (
								<Div
									d="flex"
									align="center"
									style={{ gap: "0.5rem" }}
									p="0.5rem 0.75rem"
									rounded="15px"
									bg={theme.colors.yellow_light}>
									<FontAwesomeIcon
										icon={faTriangleExclamation}
										color={theme.colors.yellow}
										size="sm"
									/>
									<Text textSize="caption" textColor={theme.colors.yellow}>
										{matches.withoutFee} {matches.withoutFee < 2 ? "volta" : "volte"}{" "}
										che hai giocato senza tariffa campo per quella data
									</Text>
								</Div>
							)}
						</Div>
					)}
				</Div>
			)}

			{/* ── DA DARE AL CIRCOLO ─────────────────────────────────────── */}
			{/* Quello che i sostituti dei corsi ti hanno pagato per conto del
			    circolo: non è tuo e non sta nel netto, ma a fine mese va
			    dato — ed è qui per non doverselo ricordare. */}
			{hasHeldForSchool && (
				<Div m={{ b: openSections.heldForSchool ? "1rem" : "0.5rem" }}>
					{(hasSchoolSection || hasPrivate || hasCourtCosts) && (
						<Divider w="2px solid" m={{ b: "0.75rem" }} />
					)}
					<SectionHeader
						icon={faHandHoldingDollar}
						color={theme.colors.gunmetal}
						bg={theme.colors.gunmetal_lighter}
						title="Da dare al circolo"
						amount={heldForSchool.total}
						expanded={openSections.heldForSchool}
						onToggle={() => toggleSection("heldForSchool")}
					/>
					{openSections.heldForSchool &&
						Object.entries(heldForSchool.byPlayer)
							.sort(([, a], [, b]) => b.amount - a.amount)
							.map(([playerID, entry]) => (
								<Row
									key={playerID}
									label={entry.playerName}
									detail={heldForSchoolDetail(entry)}
									amount={entry.amount}
									color={theme.colors.gunmetal}
								/>
							))}
					{openSections.heldForSchool && (
						<Text textSize="caption" textColor={theme.colors.text_light} m={{ t: "0.5rem" }}>
							Pagato a te da chi nei corsi viene la volta: è del circolo, e non è nel
							netto.
						</Text>
					)}
				</Div>
			)}

			{/* ── TOTALE ────────────────────────────────────────────────── */}
			<Divider w="2px solid" m={{ b: "0.75rem" }} />
			<Div
				d="flex"
				align="center"
				justify="space-between"
				p="0.6rem 0.75rem"
				rounded="15px"
				bg={theme.colors.gunmetal_lighter}>
				<Text textSize="subheader" textWeight="bold" textColor={theme.colors.gunmetal}>
					Totale netto
				</Text>
				<Text textSize="title" textWeight="bold" textColor={theme.colors.gunmetal}>
					{formatAmount(net)} €
				</Text>
			</Div>
		</Card>
	)
}

export default EarningsCard
